// UC-11 (plan.md §4, decisión H de tasks.md). `onAssociationChanged` es el `refresh` de
// `useClientCompanies` (para refrescar `datacenters_count` en la tabla), cableado por el
// contenedor. UC-17 api-v1-stabilization mantiene list/add y no expone unlink sin endpoint v1.

import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { clientCompanyRepository } from '../infrastructure/clientCompany.repository';
import { datacenterOptionsRepository } from '../infrastructure/datacenterOptions.repository';
import {
  toDatacenterAssociationPayload,
  validateDatacenterAssociation,
} from '../domain/clientCompany.rules';
import { apiErrorMessage } from './apiError';
import type { Id } from '../domain/enterprise.types';
import type { DatacenterOption } from '../domain/options.types';
import type {
  ClientCompany,
  CompanyDatacenterAssociation,
  DatacenterAssociationFormValues,
  DatacenterKind,
} from '../domain/clientCompany.types';

const EMPTY_ASSOC_FORM: DatacenterAssociationFormValues = {
  kind: 'kyndryl',
  datacenter_id: '',
  client_datacenter_id: '',
  notes: '',
};

export function useCompanyDatacenters(clientId: Id, onAssociationChanged?: () => void) {
  const { enqueueSnackbar } = useSnackbar();

  const [company, setCompany] = useState<ClientCompany | null>(null);
  const [associations, setAssociations] = useState<CompanyDatacenterAssociation[]>([]);
  const [loading, setLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<DatacenterAssociationFormValues>(EMPTY_ASSOC_FORM);
  const [kyndrylOptions, setKyndrylOptions] = useState<DatacenterOption[]>([]);
  const [clientOptions, setClientOptions] = useState<DatacenterOption[]>([]);
  const [saving, setSaving] = useState(false);

  async function open(c: ClientCompany) {
    setCompany(c);
    setLoading(true);
    try {
      setAssociations(await clientCompanyRepository.getDatacenters(clientId, c.id));
    } catch {
      enqueueSnackbar('Error al cargar datacenters asociados', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }

  function close() {
    setCompany(null);
  }

  async function openAdd() {
    setAddForm(EMPTY_ASSOC_FORM);
    setAddOpen(true);
    try {
      const [kyndryl, client] = await Promise.all([
        datacenterOptionsRepository.getKyndrylDatacenters(),
        datacenterOptionsRepository.getClientDatacenters(clientId),
      ]);
      setKyndrylOptions(kyndryl);
      setClientOptions(client);
    } catch {
      enqueueSnackbar('Error al cargar catálogo de datacenters', { variant: 'error' });
    }
  }

  function closeAdd() {
    setAddOpen(false);
  }

  function setAddKind(kind: DatacenterKind) {
    setAddForm((prev) => ({ ...prev, kind, datacenter_id: '', client_datacenter_id: '' }));
  }

  function setAddField<K extends 'datacenter_id' | 'client_datacenter_id' | 'notes'>(
    field: K,
    value: string
  ) {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  }

  async function saveAssociation() {
    if (!company) return;
    const validation = validateDatacenterAssociation(addForm);
    if (!validation.ok) {
      enqueueSnackbar(
        validation.error === 'datacenterRequired'
          ? 'Selecciona un datacenter'
          : 'Selecciona un datacenter del cliente',
        { variant: 'warning' }
      );
      return;
    }
    const payload = toDatacenterAssociationPayload(addForm);
    setSaving(true);
    try {
      await clientCompanyRepository.addDatacenter(clientId, company.id, payload);
      enqueueSnackbar('Datacenter asociado', { variant: 'success' });
      setAddOpen(false);
      setAssociations(await clientCompanyRepository.getDatacenters(clientId, company.id));
      onAssociationChanged?.();
    } catch (err) {
      enqueueSnackbar(apiErrorMessage(err) || 'Error al asociar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }

  return {
    company,
    associations,
    loading,
    open,
    close,
    addOpen,
    addForm,
    setAddKind,
    setAddField,
    kyndrylOptions,
    clientOptions,
    openAdd,
    closeAdd,
    saveAssociation,
    saving,
  };
}
